// =============================================================================
// A proposta da área em planilha (item 2.2 do pedido de 20/10: "com opção de
// importar planilha")
//
// O gestor baixa a proposta como está (CSV com ponto e vírgula, que o Excel em
// português abre em colunas), preenche e importa de volta (.csv ou .xlsx). A
// leitura não grava nada: ela diz o que muda, e a tela leva para o editor, onde
// o gestor confere e salva o rascunho. Regras, decididas com o Enzo em 22/10:
//
//   · a linha já enviada ou aprovada fica como está — a planilha não destrava;
//   · a linha da proposta que não está na planilha fica como está — a planilha
//     não apaga;
//   · categoria nova só entra se o código e o nome conferem com o plano de
//     contas, como nas outras importações (021): casar só pelo código já
//     classificou Apostilas como Prêmios.
//   · o gasto do ano base vai na planilha só para consulta, e é ignorado na
//     volta: o realizado não se edita.
//   · a pessoa e a subcategoria (082) vão na última coluna, e na volta só
//     acertam a linha que já existe: elas vêm do ano base, a planilha não as
//     cria. A coluna é lida pelo nome, então a planilha baixada antes dela
//     continua valendo.
// =============================================================================

import { centavosParaCsv } from "./csv.ts";
import { paraCentavos } from "./dinheiro.ts";
import { editavel, MESES, soma, type SituacaoLinha } from "./proposta.ts";

const normalizar = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/\s+/g, " ").trim();

/** O que a planilha precisa de cada linha da proposta. */
export type LinhaDaProposta = {
  categoria_id: string;
  categoria_codigo: string | null;
  categoria_nome: string;
  descricao: string | null;
  /** A pessoa ou a subcategoria da linha (082); nula na categoria e no item novo. */
  detalhe?: string | null;
  base: string[];
  pedido: string[];
  observacao: string;
  situacao: SituacaoLinha;
};

export function cabecalhoProposta(ano: number): string[] {
  return ["Código", "Categoria", "Item novo", ...MESES, "Explicação (na linha da categoria)", `Gasto em ${ano - 1} (só consulta)`,
    "Pessoa ou subcategoria (não mude)"];
}

/** Na categoria: a linha dela, as pessoas ou subcategorias, e os itens novos. */
const ordem = (l: LinhaDaProposta) => (l.descricao !== null ? 2 : l.detalhe ? 1 : 0);

/** As linhas da planilha que o gestor baixa: a proposta como está, categoria antes dos itens dela. */
export function montarPlanilhaProposta(linhas: LinhaDaProposta[], ano: number): string[][] {
  const ordenadas = [...linhas].sort((a, b) =>
    (a.categoria_codigo ?? a.categoria_nome).localeCompare(b.categoria_codigo ?? b.categoria_nome, undefined, { numeric: true })
    || ordem(a) - ordem(b)
    || (a.detalhe ?? a.descricao ?? "").localeCompare(b.detalhe ?? b.descricao ?? ""));
  return [
    cabecalhoProposta(ano),
    ...ordenadas.map((l) => [
      l.categoria_codigo ?? "", l.categoria_nome, l.descricao ?? "",
      ...l.pedido.map((v) => centavosParaCsv(v)), l.observacao, centavosParaCsv(soma(l.base).toString()), l.detalhe ?? "",
    ]),
  ];
}

export type CategoriaConhecida = { id: string; codigo: string | null; nome: string };

export type LinhaImportada = {
  categoria_id: string;
  categoria_codigo: string | null;
  categoria_nome: string;
  descricao: string | null;
  detalhe: string | null;
  pedido: string[];
  observacao: string;
  /** A linha na planilha, contando a do cabeçalho como 1. */
  linha: number;
};

export type LeituraProposta = { linhas: LinhaImportada[]; erros: string[] };

/** A célula como texto. O .xlsx devolve número: vira "1234,50", que o `paraCentavos` lê. */
function texto(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return Number.isFinite(v) ? v.toFixed(2).replace(".", ",") : "";
  return String(v).trim();
}

const chaveDaLinha = (categoria: string, descricao: string | null, detalhe: string | null | undefined) =>
  `${categoria}|${normalizar(descricao ?? "")}|${normalizar(detalhe ?? "")}`;

/** Lê a planilha (já dividida em células) contra as categorias que recebem orçamento. */
export function lerPlanilhaProposta(tabela: unknown[][], categorias: CategoriaConhecida[]): LeituraProposta {
  const erros: string[] = [];
  const iCab = tabela.findIndex((r) => {
    const n = r.map((c) => normalizar(texto(c)));
    return n.includes("categoria") && n.includes("jan");
  });
  if (iCab < 0) {
    return { linhas: [], erros: ["Não achei o cabeçalho: a planilha precisa das colunas Categoria e Jan a Dez, como a que o Nexa baixa."] };
  }
  const cab = tabela[iCab]!.map((c) => normalizar(texto(c)));
  const col = {
    codigo: cab.findIndex((c) => c === "codigo"),
    categoria: cab.indexOf("categoria"),
    item: cab.findIndex((c) => c.startsWith("item")),
    explicacao: cab.findIndex((c) => c.startsWith("explica")),
    detalhe: cab.findIndex((c) => c.startsWith("pessoa")),
    meses: MESES.map((m) => cab.indexOf(normalizar(m))),
  };
  const faltam = MESES.filter((_, i) => col.meses[i]! < 0);
  if (faltam.length) return { linhas: [], erros: [`Faltam as colunas de ${faltam.join(", ")}.`] };

  const porCodigo = new Map<string, CategoriaConhecida[]>();
  for (const c of categorias) if (c.codigo) porCodigo.set(c.codigo, [...(porCodigo.get(c.codigo) ?? []), c]);
  const porNome = new Map(categorias.map((c) => [normalizar(c.nome), c]));

  const linhas: LinhaImportada[] = [];
  const vistas = new Set<string>();
  tabela.slice(iCab + 1).forEach((r, k) => {
    const n = iCab + 2 + k;
    const cel = (i: number) => (i >= 0 ? texto(r[i]) : "");
    const codigo = cel(col.codigo), nome = cel(col.categoria), item = cel(col.item);
    const valores = col.meses.map((i) => cel(i));
    if (!codigo && !nome && !item && valores.every((v) => !v)) return;

    // A categoria pelo código E pelo nome, como as outras importações.
    let cat: CategoriaConhecida | undefined;
    if (codigo) {
      const doCodigo = porCodigo.get(codigo) ?? [];
      cat = doCodigo.length === 1 ? doCodigo[0] : doCodigo.find((c) => normalizar(c.nome) === normalizar(nome));
      if (!cat) {
        erros.push(`Linha ${n}: o código ${codigo} não é de uma categoria que recebe orçamento.`);
        return;
      }
      if (nome && normalizar(cat.nome) !== normalizar(nome)) {
        erros.push(`Linha ${n}: o código ${codigo} é de "${cat.nome}", e a linha diz "${nome}". Corrija um dos dois.`);
        return;
      }
    } else {
      cat = porNome.get(normalizar(nome));
      if (!cat) {
        erros.push(`Linha ${n}: a categoria "${nome}" não é uma categoria que recebe orçamento. Use o código e o nome do plano de contas.`);
        return;
      }
    }

    const pedido: string[] = [];
    for (let i = 0; i < 12; i++) {
      const v = valores[i]!;
      if (!v) { pedido.push("0"); continue; }
      const c = paraCentavos(v);
      if (c === null || BigInt(c) < 0n) {
        erros.push(`Linha ${n}: o valor de ${MESES[i]} ("${v}") não é um número válido.`);
        return;
      }
      pedido.push(c);
    }

    const descricao = item ? item : null;
    const detalhe = descricao ? null : cel(col.detalhe) || null;
    const chave = chaveDaLinha(cat.id, descricao, detalhe);
    if (vistas.has(chave)) {
      erros.push(`Linha ${n}: ${descricao ? `o item "${descricao}"` : detalhe ? `"${detalhe}"` : `a categoria "${cat.nome}"`} aparece mais de uma vez.`);
      return;
    }
    vistas.add(chave);
    linhas.push({
      categoria_id: cat.id, categoria_codigo: cat.codigo, categoria_nome: cat.nome, descricao, detalhe, pedido,
      observacao: cel(col.explicacao), linha: n,
    });
  });
  return { linhas, erros };
}

export type Mudanca = {
  rotulo: string;
  tipo: "alterada" | "nova_categoria" | "novo_item" | "travada" | "sem_par";
  antes: bigint;
  depois: bigint;
};

export type Comparacao<T> = { resultado: T[]; mudancas: Mudanca[]; ausentes: number };

/**
 * O que a planilha muda na proposta. `nova` cria a linha do editor para o que
 * não existia. Nada aqui grava: o resultado vai para o editor.
 */
export function compararImportacao<T extends LinhaDaProposta>(
  atuais: T[], importadas: LinhaImportada[], nova: (l: LinhaImportada) => T,
): Comparacao<T> {
  const porChave = new Map(importadas.map((l) => [chaveDaLinha(l.categoria_id, l.descricao, l.detalhe), l]));
  const mudancas: Mudanca[] = [];
  const usadas = new Set<string>();
  const rotulo = (l: { categoria_codigo: string | null; categoria_nome: string; descricao: string | null; detalhe?: string | null }) =>
    `${l.categoria_codigo ? `${l.categoria_codigo} ` : ""}${l.categoria_nome}${l.descricao ? ` · ${l.descricao}` : l.detalhe ? ` · ${l.detalhe}` : ""}`;

  let ausentes = 0;
  const resultado = atuais.map((a) => {
    const k = chaveDaLinha(a.categoria_id, a.descricao, a.detalhe);
    const imp = porChave.get(k);
    if (!imp) { ausentes++; return a; }
    usadas.add(k);
    const igual = imp.pedido.every((v, i) => BigInt(v) === BigInt(a.pedido[i] ?? "0")) && imp.observacao === a.observacao;
    if (igual) return a;
    if (!editavel(a.situacao)) {
      mudancas.push({ rotulo: rotulo(a), tipo: "travada", antes: soma(a.pedido), depois: soma(imp.pedido) });
      return a;
    }
    mudancas.push({ rotulo: rotulo(a), tipo: "alterada", antes: soma(a.pedido), depois: soma(imp.pedido) });
    return { ...a, pedido: imp.pedido, observacao: imp.observacao };
  });
  for (const imp of importadas) {
    const k = chaveDaLinha(imp.categoria_id, imp.descricao, imp.detalhe);
    if (usadas.has(k)) continue;
    // A pessoa e a subcategoria vêm do ano base: a planilha não as cria.
    if (imp.detalhe) {
      mudancas.push({ rotulo: rotulo(imp), tipo: "sem_par", antes: 0n, depois: soma(imp.pedido) });
      continue;
    }
    resultado.push(nova(imp));
    mudancas.push({ rotulo: rotulo(imp), tipo: imp.descricao ? "novo_item" : "nova_categoria", antes: 0n, depois: soma(imp.pedido) });
  }
  return { resultado, mudancas, ausentes };
}
